import { createSlice, CaseReducer, PayloadAction } from "@reduxjs/toolkit";
import { IUserData } from "@Interfaces";
import { AccountTypes } from "@Interfaces/Entities/User";

interface IAuthStore {
    user: IUserData;
    jwt_token: string | null;
    jwt_token_expiry: string | null;
    hasErrors: boolean;
    isAuthenticated: boolean;
    isLoading: boolean;
    snackbarOpen: boolean;
    snackbarType: string | null;
    snackbarMessage: string | null;
    isAccountTaken: boolean;
}

const initialState: IAuthStore = {
    user: {
        username: "",
        id: "",
        gender: "",
        email: "",
        avatar: { public_url: "", fid: "" },
        lang: "",
        profession: "",
        account_type: "freemium",
        account: {
            type: AccountTypes.Freemium,
            event_limit: null,
            total_events_created: 0,
        },
        restrictions: {
            data_reports: 0,
            branded_qr: 0,
        },
    },
    jwt_token: null,
    jwt_token_expiry: null,
    hasErrors: false,
    isAuthenticated: false,
    isLoading: true,
    snackbarOpen: false,
    snackbarType: "success",
    snackbarMessage: "",
    isAccountTaken: false,
};

const authSuccess: CaseReducer<IAuthStore> = (state) => {
    state.isAuthenticated = true;
    state.hasErrors = false;
    state.isLoading = false;
};

const authInvalid: CaseReducer<IAuthStore> = (state) => {
    state.isAuthenticated = false;
    state.hasErrors = false;
    state.isLoading = false;
};

const authFailure: CaseReducer<IAuthStore> = (state) => {
    state.hasErrors = true;
    state.isAuthenticated = false;
};

const authSnackbar: CaseReducer<IAuthStore, PayloadAction<any>> = (
    state,
    { payload }
) => {
    state.snackbarOpen = payload.snackbarOpen;
    state.snackbarType = payload.snackbarType;
    state.snackbarMessage = payload.snackbarMessage;
};

const resetErrors: CaseReducer<IAuthStore> = (state) => {
    state.hasErrors = false;
    state.snackbarOpen = false;
    state.snackbarType = "";
    state.snackbarMessage = "";
};

const isLoaded: CaseReducer<IAuthStore> = (state) => {
    state.isLoading = false;
};

const isLoading: CaseReducer<IAuthStore> = (state) => {
    state.isLoading = true;
};

const setAccessData: CaseReducer<IAuthStore, PayloadAction<any>> = (
    state,
    { payload }
) => {
    state.jwt_token = payload.jwt_token;
    state.user = payload.user;
    state.jwt_token_expiry = payload.jwt_token_expiry;
};

const setAccountIsTaken: CaseReducer<IAuthStore, PayloadAction<boolean>> = (
    state,
    { payload }
) => {
    state.isAccountTaken = payload;
};

const updateUserData: CaseReducer<IAuthStore, PayloadAction<any>> = (
    state,
    { payload }
) => {
    state.user = payload;
};

const setUserRestrictions: CaseReducer<
    IAuthStore,
    PayloadAction<Partial<any>>
> = (state, { payload }) => {
    state.user = { ...state.user, ...payload };
};

const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        authSnackbar,
        setAccountIsTaken,
        authInvalid,
        authFailure,
        authSuccess,
        setAccessData,
        isLoaded,
        resetErrors,
        isLoading,
        updateUserData,
        setUserRestrictions,
    },
});

export const { name, actions } = authSlice;

export default authSlice.reducer;
